// ============================================================================
//  MarkModal.jsx — the monthly Mile Marker check-in.
//
//  One entry per goal per month; re-logging the same month updates it.
// ============================================================================

import React, { useState } from "react";
import { X, Save } from "lucide-react";
import {
  C, RAG, display, kicker, overlay, sheet, lbl, inp, primaryBtn, iconBtn,
} from "../theme/tokens.js";
import { monthKey, monthLabel } from "../lib/metrics.js";

export default function MarkModal({ goal, existing, onSave, onClose }) {
  const [pct, setPct] = useState(existing?.pct ?? 0);
  const [rag, setRag] = useState(existing?.rag ?? "green");
  const [note, setNote] = useState(existing?.note ?? "");
  const [blockers, setBlockers] = useState(existing?.blockers ?? "");
  const [commitment, setCommitment] = useState(existing?.commitment ?? "");

  return (
    <div onClick={onClose} style={overlay}>
      <div onClick={(e) => e.stopPropagation()} style={{ ...sheet, maxWidth: 540 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={kicker}>{monthLabel(monthKey())} · Mile Marker</div>
            <h3 style={{ ...display, fontSize: 22, margin: "8px 0 0", color: C.text }}>{goal.title}</h3>
          </div>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        <div style={{ marginTop: 24 }}>
          <label style={lbl}>Progress — <span style={{ color: C.red }}>{pct}%</span></label>
          <input type="range" min="0" max="100" value={pct}
            onChange={(e) => setPct(+e.target.value)}
            style={{ width: "100%", accentColor: C.red, marginTop: 10 }} />
        </div>

        <div style={{ marginTop: 20 }}>
          <label style={lbl}>Status</label>
          <div style={{ display: "flex", gap: 8, marginTop: 9 }}>
            {Object.entries(RAG).map(([k, v]) => (
              <button key={k} onClick={() => setRag(k)} style={{
                flex: 1, padding: "10px 8px", borderRadius: 8, cursor: "pointer",
                border: rag === k ? `1.5px solid ${v.dot}` : `1.5px solid ${C.line}`,
                background: rag === k ? v.soft : "transparent",
                color: rag === k ? v.text : C.sub, fontWeight: 700, fontSize: 12.5,
                letterSpacing: ".03em", transition: "all 160ms" }}>{v.label}</button>
            ))}
          </div>
        </div>

        <Field label="Status note — where are you on this?" value={note} set={setNote}
          ph="Honest assessment of where this goal stands this month…" />
        <Field label="Blockers — what's in the way?" value={blockers} set={setBlockers}
          ph="Obstacles, friction, anything slowing you down…" />
        <Field label="Next-month commitment — what will you do before we meet again?" value={commitment} set={setCommitment}
          ph="The specific action you're committing to before the next Mile Marker…" />

        <button onClick={() => onSave({ pct, rag, note, blockers, commitment })}
          style={{ ...primaryBtn, width: "100%", marginTop: 26 }}>
          <Save size={16} /> Log this month's Mile Marker
        </button>
      </div>
    </div>
  );
}

function Field({ label, value, set, ph }) {
  return (
    <div style={{ marginTop: 18 }}>
      <label style={lbl}>{label}</label>
      <textarea value={value} onChange={(e) => set(e.target.value)} placeholder={ph}
        style={{ ...inp, minHeight: 60, resize: "vertical", lineHeight: 1.5 }} />
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  COACH PANEL — Anthropic-backed SMART goal coach                    */
/* ------------------------------------------------------------------ */

// Tiny markdown renderer — supports **bold**, [text](url), and `- ` bullets.
// Deliberately narrow scope; a full lib would be overkill for the coach's
